import { cva, VariantProps } from 'class-variance-authority';
import {
  forwardRef,
  memo,
  useState,
  useRef,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  FocusEvent,
  useMemo,
} from 'react';
import { XIcon } from '@page/icons';
import { IconX } from '@tabler/icons-react';

import { useMergedRefs } from '@page/core/hooks/common';
import { cn } from '../../lib/styles';
import { Label } from '../../designSystem/Label';
import { IconButton } from '../../designSystem/IconButton';
import { Popover, PopoverTrigger, PopoverContent } from '../../designSystem/Popover';
import { MenuItem } from '../../designSystem/MenuItem';

const tagInputVariants = cva(
  cn(
    'font-body-sm font-medium text-text-primary placeholder:text-text-tertiary transition-all',
    'flex flex-wrap w-full gap-1 relative group items-start',
  ),
  {
    variants: {
      variant: {
        default: ['bg-bg-inter-secondary-default rounded-sm border border-bg-inter-secondary-default'].join(' '),
        unstyled: 'border-none outline-none',
      },
      size: {
        md: 'py-1 min-h-10 px-2',
        sm: 'py-0.75 min-h-9 px-1',
      },
      readOnly: {
        false: '',
        true: '',
      },
    },
    compoundVariants: [
      {
        variant: 'default',
        readOnly: false,
        className:
          'focus-within:border-border-input-select hover:bg-bg-inter-secondary-hover focus-within:bg-bg-inter-secondary-default!',
      },
    ],
    defaultVariants: {
      variant: 'default',
      size: 'md',
      readOnly: false,
    },
  },
);

type TagProps = React.ComponentProps<'button'> & {
  tag: string;
  icon?: React.ReactNode;
  onRemove?: (tag: string) => void;
  onTagClick?: TagInputProps['onTagClick'];
  disabled?: boolean;
  className?: string;
  size?: 'md' | 'sm';
};

// Gap between tags in pixels
const TAGS_GAP = 4;
// Width of the input area when tags overflowed the container in scroll and slice modes
const INPUT_AREA_MIN_WIDTH = 40;
const OVERFLOW_TAG_EST_WIDTH = 40;
const EXPAND_TAG_EST_WIDTH = 105;

const Tag = memo(
  forwardRef<HTMLDivElement, TagProps>(
    ({ tag, icon, onRemove, onTagClick, onClick, disabled, size = 'md', className }, ref) => {
      const handleClick = useCallback(
        (e: React.MouseEvent<HTMLButtonElement>) => {
          onTagClick?.(tag, e, !!onRemove);
          onClick?.(e);
        },
        [onTagClick, tag, onRemove, onClick],
      );
      return (
        // Non-interactive wrapper so the clickable chip body and the remove control are siblings
        // rather than a button nested inside a button (axe: nested-interactive).
        <div
          ref={ref}
          className={cn(
            'inline-flex items-center gap-1 p-2 rounded-sm font-body-sm font-normal',
            'bg-bg-sur-primary hover:bg-gray-2',
            'transition-all',
            disabled && 'opacity-50 cursor-not-allowed',
            'shrink-0',
            {
              'p-2': size === 'md',
              'py-0.75 pl-2 pr-1.5': size === 'sm',
            },
            className,
          )}
        >
          <button
            type="button"
            onClick={handleClick}
            tabIndex={-1}
            className="flex min-w-0 cursor-[inherit] items-center bg-transparent"
          >
            {icon}
            <span className="text-text-secondary truncate max-w-32">{tag}</span>
          </button>
          {onRemove && (
            <IconButton
              size="sm"
              onClick={() => onRemove(tag)}
              disabled={disabled}
              className="h-4 w-4 p-0"
              aria-label={`Remove ${tag}`}
            >
              <XIcon className="size-4 fill-icon-secondary" />
            </IconButton>
          )}
        </div>
      );
    },
  ),
);

type OverflowCountTagProps = {
  tags: string[];
  onRemove?: (tag: string) => void;
  showPopover?: boolean;
  onClick?: (currentPopoverOpen: boolean, e: React.MouseEvent<HTMLButtonElement>) => void;
  getTagIcon?: (tag: string) => React.ReactNode;
} & Pick<TagProps, 'className' | 'size'>;

const OverflowCountTag = memo(
  forwardRef<HTMLDivElement, OverflowCountTagProps>(
    ({ tags, className, onRemove, showPopover = true, onClick, size, getTagIcon }, ref) => {
      const [open, setOpen] = useState(false);

      const handleClick = useCallback(
        (e: React.MouseEvent<HTMLButtonElement>) => {
          onClick?.(open, e);
        },
        [onClick, open],
      );

      const tagElement = (
        <Tag
          ref={ref}
          tag={`+${tags.length}`}
          size={size}
          className={cn(
            'pr-2',
            {
              'cursor-pointer': showPopover,
            },
            className,
          )}
          onClick={handleClick}
        />
      );

      if (!showPopover) {
        return tagElement;
      }

      return (
        <>
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger>{tagElement}</PopoverTrigger>
            <PopoverContent className="p-1.5 w-auto min-w-60 font-body-sm font-medium text-text-secondary fill-text-secondary max-h-[400px] overflow-y-auto">
              {tags.map((tag) => (
                <MenuItem
                  key={tag}
                  className="justify-between font-body-sm font-medium text-text-secondary"
                  onClick={() => onRemove?.(tag)}
                >
                  {getTagIcon?.(tag)}
                  <span>{tag}</span>
                  <XIcon className="size-4" />
                </MenuItem>
              ))}
            </PopoverContent>
          </Popover>
        </>
      );
    },
  ),
);

export type TagInputOverflowMode = 'wrap' | 'scroll' | 'slice';

export type TagInputProps = Omit<React.ComponentProps<'input'>, 'size' | 'ref' | 'value'> &
  VariantProps<typeof tagInputVariants> & {
    label?: React.ReactNode;
    error?: React.ReactNode;
    /** Direct container of the input */
    inputContainerClassName?: string;
    inputClassName?: string;
    tags?: string[];
    onTagsChange?: (tags: string[], targetTag: string) => void;
    maxTags?: number;
    tagClassName?: string;
    placeholder?: string;
    disabled?: boolean;
    /** Custom left icon to show before tags */
    leftIcon?: React.ReactNode;
    leftIconClassName?: string;
    clearable?: boolean;
    forceShowClearButton?: boolean;
    onClear?: () => void;
    clearButtonClassName?: string;
    tagsContainerClassName?: string;
    /** Display mode when overflowing: wrap (default), scroll, or slice (shows "+n" for overflow) */
    overflow?: TagInputOverflowMode;
    showOverflowTagsMode?: 'none' | 'popover' | 'expand';
    onOverflowCountTagClick?: OverflowCountTagProps['onClick'];
    getTagIcon?: (tag: string) => React.ReactNode;
    addTagOnBlur?: boolean;
    inputValue?: string;
    onInputChange?: (value: string) => void;
    onTagClick?: (tag: string, e: React.MouseEvent<HTMLButtonElement>, isRemovable: boolean) => void;
    labelVariant?: 'default' | 'inline';
    size?: 'md' | 'sm';
    hideError?: boolean;
    onInternalOverflowChange?: (internalOverflow: TagInputOverflowMode) => void;
  };

const TagInputBase = (
  {
    id,
    className,
    type = 'text',
    variant,
    label,
    error,
    inputContainerClassName,
    inputClassName,
    tags = [],
    onTagsChange,
    maxTags,
    tagClassName,
    onTagClick,
    placeholder = 'Type and press Enter to add tags...',
    disabled,
    leftIcon,
    leftIconClassName,
    clearable,
    forceShowClearButton,
    onClear,
    clearButtonClassName,
    tagsContainerClassName,
    overflow: overflowProp = 'wrap',
    showOverflowTagsMode = 'popover',
    addTagOnBlur = true,
    inputValue: inputValueProp,
    onInputChange,
    onChange,
    onBlur,
    onKeyDown,
    onOverflowCountTagClick,
    onInternalOverflowChange,
    getTagIcon,
    labelVariant = 'default',
    size = 'md',
    readOnly = false,
    hideError = false,
    ...rest
  }: TagInputProps,
  forwardedRef: React.ForwardedRef<HTMLInputElement>,
) => {
  const generatedId = useId();
  // A stable id is required so the visible <Label htmlFor> actually associates with the input.
  const actualId = id || rest.name || generatedId;
  const [_inputValue, _setInputValue] = useState('');
  const internalRef = useRef<HTMLInputElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const tagsContainerRef = useRef<HTMLDivElement | null>(null);
  const inlineLabelRef = useRef<HTMLLabelElement | null>(null);
  const tagRefs = useRef<Map<number, HTMLElement>>(new Map());
  const [visibleTagCount, setVisibleTagCount] = useState<number>(tags.length);
  const [overflow, setOverflow] = useState<TagInputProps['overflow']>(overflowProp);

  const inputValue = inputValueProp ?? _inputValue;

  const setInputValue = useCallback(
    (value: string) => {
      _setInputValue(value);
      onInputChange?.(value);
    },
    [onInputChange],
  );

  const ref = useMergedRefs<HTMLInputElement>(forwardedRef, internalRef);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      setInputValue(newValue);
      onChange?.(e);
      onInputChange?.(newValue);
    },
    [onChange, onInputChange, setInputValue],
  );

  const addTag = useCallback(
    (tag: string) => {
      const trimmedTag = tag.trim();
      if (trimmedTag && !tags.includes(trimmedTag) && (!maxTags || tags.length < maxTags)) {
        const newTags = [...tags, trimmedTag];
        onTagsChange?.(newTags, trimmedTag);
        setInputValue('');
        onInputChange?.('');
      }
    },
    [tags, maxTags, onTagsChange, setInputValue, onInputChange],
  );

  const removeTag = useCallback(
    (tagToRemove: string) => {
      const newTags = tags.filter((tag) => tag !== tagToRemove);
      onTagsChange?.(newTags, tagToRemove);
    },
    [tags, onTagsChange],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        addTag(inputValue);
      } else if (e.key === 'Backspace' && !inputValue && tags.length > 0) {
        e.preventDefault();
        removeTag(tags[tags.length - 1]);
      }
      onKeyDown?.(e);
    },
    [inputValue, tags, onKeyDown, addTag, removeTag],
  );

  const handleInputBlur = useCallback(
    (e: FocusEvent<HTMLInputElement>) => {
      if (addTagOnBlur && inputValue.trim()) {
        addTag(inputValue);
      }
      onBlur?.(e);
    },
    [inputValue, onBlur, addTag, addTagOnBlur],
  );

  const handleInternalOverflowChange = useCallback(
    (newOverflow: TagInputOverflowMode) => {
      setOverflow(newOverflow);
      onInternalOverflowChange?.(newOverflow);
    },
    [onInternalOverflowChange],
  );

  const handleClear = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      setInputValue('');
      onTagsChange?.([], '');
      internalRef.current?.focus();
      onClear?.();
    },
    [onClear, onTagsChange, setInputValue],
  );

  // Calculate visible tags for slice mode
  const calculateVisibleTags = useCallback(() => {
    const outerContainer = containerRef.current;
    const container = tagsContainerRef.current;
    if (!container) {
      setVisibleTagCount(tags.length);
      return;
    }

    // Try to get max-width from computed style, fall back to maxWidth prop, then container width
    let availableWidth = container.offsetWidth;
    if (outerContainer) {
      const computedMaxWidth = getComputedStyle(outerContainer).maxWidth;
      if (computedMaxWidth && computedMaxWidth !== 'none') {
        const parsedMaxWidth = parseFloat(computedMaxWidth);
        if (!isNaN(parsedMaxWidth)) {
          availableWidth = parsedMaxWidth;
        }
      }
    }

    // Account for inline label width if present
    const inlineLabelWidth = inlineLabelRef.current?.offsetWidth ?? 0;
    // Add gap between label and tags container if label exists
    const labelGap = inlineLabelWidth > 0 ? TAGS_GAP + 16 : 0;

    let usedWidth = (readOnly ? 0 : INPUT_AREA_MIN_WIDTH) + inlineLabelWidth + labelGap;
    let visibleCount = 0;

    // Iterate through tags and calculate how many can fit
    for (let i = 0; i < tags.length; i++) {
      const tagElement = tagRefs.current.get(i);
      if (!tagElement) continue;

      const tagWidth = tagElement.offsetWidth;
      const spaceNeeded = tagWidth + (visibleCount > 0 ? TAGS_GAP : 0);

      const remainingTags = tags.length - i;
      const needsOverflowIndicator = remainingTags > 1;
      const lastTagWidth = showOverflowTagsMode === 'expand' ? EXPAND_TAG_EST_WIDTH : OVERFLOW_TAG_EST_WIDTH;
      const reservedWidth = needsOverflowIndicator ? lastTagWidth + TAGS_GAP : 0;

      if (usedWidth + spaceNeeded + reservedWidth <= availableWidth) {
        usedWidth += spaceNeeded;
        visibleCount++;
      } else {
        break;
      }
    }

    setVisibleTagCount(visibleCount);
  }, [readOnly, showOverflowTagsMode, tags.length]);

  // Recalculate visible tags when dependencies change
  useLayoutEffect(() => {
    if (overflow !== 'slice') {
      return;
    }
    calculateVisibleTags();
  }, [calculateVisibleTags, overflow]);

  // Also recalculate on resize
  useEffect(() => {
    if (overflow !== 'slice') return;

    const resizeObserver = new ResizeObserver(() => {
      calculateVisibleTags();
    });

    if (tagsContainerRef.current) {
      resizeObserver.observe(tagsContainerRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [overflow, calculateVisibleTags]);

  const setTagRef = useCallback((index: number, element: HTMLElement | null) => {
    if (element) {
      tagRefs.current.set(index, element);
    } else {
      tagRefs.current.delete(index);
    }
  }, []);

  const visibleTags = useMemo(
    () => (overflow === 'slice' ? tags.slice(0, visibleTagCount) : tags),
    [overflow, tags, visibleTagCount],
  );
  const hiddenTags = useMemo(
    () => (overflow === 'slice' ? tags.slice(visibleTagCount) : []),
    [overflow, tags, visibleTagCount],
  );

  // Without a visible <Label>, the input has no accessible name; fall back to the placeholder
  // text (a caller-provided aria-label in `rest` still wins where it is spread below).
  const inputAriaLabel = !label && typeof placeholder === 'string' ? placeholder : undefined;

  const inputElement = (
    <div
      ref={containerRef}
      className={cn(
        tagInputVariants({ variant, size, readOnly }),
        {
          'overflow-hidden': overflow === 'scroll' || overflow === 'slice',
          'cursor-pointer': tags.length === 0,
          'flex-nowrap': labelVariant === 'inline',
        },
        error && 'border-text-destructive',
        disabled && 'opacity-50 cursor-not-allowed',
        !label && className,
        inputContainerClassName,
      )}
    >
      {leftIcon ? (
        <div
          className={cn('self-center translate-x-0.5', leftIconClassName)}
          onClick={() => internalRef.current?.focus()}
        >
          {leftIcon}
        </div>
      ) : null}
      {labelVariant === 'inline' && label ? (
        <Label
          ref={inlineLabelRef}
          htmlFor={actualId}
          className={cn('ml-2 mr-2 whitespace-nowrap', {
            'mt-1': readOnly,
            'mt-1.5': !readOnly,
          })}
        >
          {label}
        </Label>
      ) : null}
      <div
        ref={tagsContainerRef}
        className={cn(
          'flex items-center',
          {
            'flex-1 overflow-x-auto scrollbar-none w-full': overflow === 'scroll',
            'flex-1 flex-wrap': overflow === 'wrap',
            'flex-1 overflow-hidden': overflow === 'slice',
          },
          tagsContainerClassName,
        )}
        style={{
          gap: TAGS_GAP,
        }}
      >
        {/* Measure the width of all the tags */}
        {overflow === 'slice' && (
          <div className="absolute invisible pointer-events-none flex" style={{ gap: TAGS_GAP }} aria-hidden="true">
            {tags.map((tag, index) => (
              <Tag
                key={`${tag}-${index}`}
                ref={(el) => setTagRef(index, el)}
                tag={tag}
                icon={getTagIcon?.(tag)}
                onRemove={removeTag}
                onTagClick={onTagClick}
                disabled={disabled}
                size={size}
                className={tagClassName}
              />
            ))}
          </div>
        )}

        {visibleTags.map((tag, index) => (
          <Tag
            key={`${tag}-${index}`}
            tag={tag}
            icon={getTagIcon?.(tag)}
            onRemove={removeTag}
            onTagClick={onTagClick}
            disabled={disabled}
            size={size}
            className={tagClassName}
          />
        ))}

        {!!hiddenTags.length && showOverflowTagsMode !== 'expand' && (
          <OverflowCountTag
            size={size}
            className={tagClassName}
            tags={hiddenTags}
            onRemove={removeTag}
            getTagIcon={getTagIcon}
            showPopover={showOverflowTagsMode === 'popover'}
            onClick={onOverflowCountTagClick}
          />
        )}

        {!!hiddenTags.length && showOverflowTagsMode === 'expand' && (
          <Tag
            tag={`Show all (${tags.length})`}
            size={size}
            className={cn('pr-2', 'cursor-pointer', tagClassName)}
            onClick={() => handleInternalOverflowChange('wrap')}
          />
        )}

        {showOverflowTagsMode === 'expand' && overflowProp !== 'wrap' && overflow === 'wrap' && (
          <Tag
            tag="Show less"
            size={size}
            className={cn('pr-2', 'cursor-pointer', tagClassName)}
            onClick={() => handleInternalOverflowChange(overflowProp)}
          />
        )}

        {!readOnly && (
          <input
            ref={ref}
            id={actualId}
            type={type}
            data-slot="input"
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onBlur={handleInputBlur}
            placeholder={tags.length === 0 ? placeholder : ''}
            aria-label={inputAriaLabel}
            disabled={disabled}
            className={cn(
              'flex-1 bg-transparent border-none outline-none placeholder:text-text-tertiary font-body-sm font-medium',
              'disabled:pointer-events-none disabled:cursor-not-allowed peer',
              'py-1 h-8 w-full',
              {
                'min-w-0': overflow === 'wrap',
              },
              inputClassName,
            )}
            style={{
              minWidth: overflow === 'scroll' || overflow === 'slice' ? INPUT_AREA_MIN_WIDTH : undefined,
            }}
            {...rest}
          />
        )}
      </div>
      {clearable && (tags.length || inputValue.trim() || forceShowClearButton) && (
        <IconButton
          size="sm"
          onClick={handleClear}
          variant="tertiary"
          aria-label="Clear"
          className={cn('hover:bg-transparent', clearButtonClassName)}
        >
          <IconX className="size-4" />
        </IconButton>
      )}
    </div>
  );

  if (!label) return inputElement;

  return (
    <div className={cn('flex flex-col', className)}>
      {labelVariant === 'default' && (
        <Label htmlFor={actualId} className="mb-2">
          {label}
        </Label>
      )}

      {inputElement}

      {!hideError && <div className="h-5 pt-1 text-text-destructive font-body-sm text-left">{error}</div>}
    </div>
  );
};

export const TagInput = memo(forwardRef(TagInputBase));
